import { useEffect, useState } from "react";
import { RegisterOptions, useForm } from "react-hook-form";
import { sandboxApi } from "services/sandbox-api/api";
import { isErrorWithMessage, isFetchBaseQueryError, isValidationErrorDetails } from "services/sandbox-api/helpers";
import { useAppDispatch, useAppSelector } from "store";
import { sandboxActionCompleted, sandboxActionInvoked, selectCurrentUser } from "store/features";
import { SandboxActionType } from "types/header";
import { Alert, Button, Dialog, LabeledInput } from "@itwin/itwinui-react";
import { FormSpinner } from "../FormSpinner";
import styles from "./UserNameActionHandler.module.css";

interface FormData {
  userName?: string;
}

interface UserNameActionHandlerProps {
  actionResumeType?: SandboxActionType;
  actionResumeArgs?: { [name: string]: string };
}

export const UserNameActionHandler = ({ actionResumeType, actionResumeArgs }: UserNameActionHandlerProps) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [createUser, { isLoading, error }] = sandboxApi.useUserCreateMutation();

  const {
    handleSubmit,
    getFieldState,
    setError,
    register,
    formState: { errors, }
  } = useForm<FormData>();

  const onClose = () => {
    setIsModalOpen(false);
    setTimeout(() => dispatch(sandboxActionCompleted()), 200);
  };

  const onSubmit = ({ userName }: FormData) => {
    createUser({ username: userName! }).unwrap().then(() => {
      onClose();
      if (actionResumeType)
        setTimeout(() => dispatch(sandboxActionInvoked({ type: actionResumeType, args: actionResumeArgs })), 200);
    });
  };

  useEffect(() => {
    setTimeout(() => setIsModalOpen(true), 5);
  }, []);

  useEffect(() => {
    if (!error)
      return;

    if (isFetchBaseQueryError(error) && isValidationErrorDetails(error.data))
      setError("userName", { message: Object.values(error.data.errors).join("\n") });
    else if (isFetchBaseQueryError(error))
      setErrorMessage(JSON.stringify(error.data))
    else if (isErrorWithMessage(error))
      setErrorMessage(error.message);
    else
      setErrorMessage("Failed to save");
  }, [error]);

  return (
    <Dialog isOpen={isModalOpen} onClose={onClose} closeOnEsc closeOnExternalClick preventDocumentScroll trapFocus setFocus isDismissible>
      <Dialog.Backdrop />
      <Dialog.Main>
        <Dialog.TitleBar titleText="Set User Name" />
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Dialog.Content>
            <div className={styles.input}>
              <LabeledInput
                label="User Name"
                defaultValue={user ? `${user.firstName}${user.lastName}` : undefined}
                status={getFieldState("userName").invalid ? "negative" : undefined}
                message={errors.userName?.message}
                {...register("userName", validationRules)} />
            </div>
            {errorMessage
              ? <Alert type="negative">{errorMessage}</Alert>
              : <Alert type="informational">Enter your public user name to be <br />associated with your Sandboxes</Alert>}
          </Dialog.Content>
          <Dialog.ButtonBar>
            {isLoading && <FormSpinner message="Saving..." />}
            <Button styleType="high-visibility" type="submit" disabled={getFieldState("userName").invalid}>Save</Button>
            <Button onClick={onClose}>Cancel</Button>
          </Dialog.ButtonBar>
        </form>
      </Dialog.Main>
    </Dialog>
  )
}

const validationRules: RegisterOptions = {
  required: {
    value: true,
    message: "User name is required"
  },
  minLength: {
    value: 3,
    message: "User name is too short"
  },
  maxLength: {
    value: 50,
    message: "User name is too long"
  },
  pattern: {
    value: /^[[a-zA-Z0-9-]*$/,
    message: "Only alphanumeric characters are allowed"
  },
};
